import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { columns } from "@/components/ui/table/columns/questions";
import { DataTable } from "@/components/ui/table/data-table.jsx";
import Loader from "@/components/utils/loader.jsx";
import { useStore } from "@/store";
import { ArrowUpDown, Plus } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CreateQuestion from "./create-question.jsx";
import UpdateQuestion from "./update-question.jsx";
import {getForms, getQuestionsByForm} from "@/api/forms-questions/forms-questions-services.js";

const FormsQuestions = ({ formType }) => {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const loggedInUser = useStore((state) => state.loggedInUser);
  const rolOptions = useStore((state) => state.rolOptions);
  const getRoles = useStore((state) => state.getRoles);
  
  const updateModal = useStore((state) => state.updateModal);
  const setUpdateModal = useStore((state) => state.setUpdateModal);
  const [createModal, setCreateModal] = useState(false);
  const selectedLanguage = useStore((state) => state.selectedLanguage);
  const setRefreshQuestions = useStore((state) => state.setRefreshQuestions);

  const rolesNeedLookup = Array.isArray(loggedInUser?.roles)
    ? loggedInUser.roles.some(
        (role) =>
          (typeof role === "number" && Number.isFinite(role)) ||
          (role &&
            typeof role === "object" &&
            typeof role.name !== "string" &&
            typeof role.role !== "string" &&
            typeof role.slug !== "string" &&
            typeof role.type !== "string"),
      )
    : false;

  useEffect(() => {
    if (
      rolesNeedLookup &&
      Array.isArray(rolOptions) &&
      rolOptions.length === 0 &&
      typeof getRoles === "function"
    ) {
      getRoles();
    }
  }, [rolesNeedLookup, rolOptions, getRoles]);

  let persistedUser = null;
  try {
    const rawStore = localStorage.getItem("store");
    if (rawStore) {
      const maybeParsed = JSON.parse(rawStore);
      const parsed =
        typeof maybeParsed === "string" ? JSON.parse(maybeParsed) : maybeParsed;

      persistedUser = parsed?.state?.loggedInUser || parsed?.loggedInUser || null;
    }
  } catch (error) {
    console.error("Error reading persisted user:", error);
  }

  const effectiveUser = loggedInUser?.roles ? loggedInUser : persistedUser;
  const effectiveRoles = effectiveUser?.roles;
  const rolesArray = Array.isArray(effectiveRoles)
    ? effectiveRoles
    : effectiveRoles
      ? [effectiveRoles]
      : [];

  const normalizedRoles = rolesArray.length
    ? rolesArray
        .map((role) => {
          if (typeof role === "string") return role;

          if (typeof role === "number" && Number.isFinite(role)) {
            const found = Array.isArray(rolOptions)
              ? rolOptions.find((option) => option?.id === role)
              : undefined;
            if (typeof found?.name === "string") return found.name;
            return String(role);
          }

          if (role && typeof role === "object") {
            if (typeof role.name === "string") return role.name;
            if (typeof role.role === "string") return role.role;
            if (typeof role.slug === "string") return role.slug;
            if (typeof role.type === "string") return role.type;
            if (typeof role.id === "number" && Number.isFinite(role.id)) {
              const found = Array.isArray(rolOptions)
                ? rolOptions.find((option) => option?.id === role.id)
                : undefined;
              if (typeof found?.name === "string") return found.name;
              return String(role.id);
            }
          }

          return "";
        })
        .map((role) => String(role).trim().toLowerCase())
        .filter(Boolean)
    : [];

  const isSuperadmin = normalizedRoles.includes("superadmin");

  useEffect(() => {
    console.info("[FormsQuestions]", {
      formType,
      storeRoles: loggedInUser?.roles,
      persistedRoles: persistedUser?.roles,
      effectiveRoles,
      rolOptionsCount: Array.isArray(rolOptions) ? rolOptions.length : null,
      normalizedRoles,
      isSuperadmin,
    });
  }, [
    formType,
    loggedInUser?.roles,
    persistedUser?.roles,
    effectiveRoles,
    rolOptions,
    normalizedRoles,
    isSuperadmin,
  ]);

  const columnsToRender = isSuperadmin
    ? [
        {
          accessorKey: "id",
          header: ({ column }) => {
            return (
              <Button
                className="uppercase text-xs"
                variant="ghost"
                onClick={() =>
                  column.toggleSorting(column.getIsSorted() === "asc")
                }
              >
                ID
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            );
          },
          cell: ({ row }) => <div className="ml-4">{row.getValue("id")}</div>,
        },
        ...columns,
      ]
    : columns;

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      // Obtener los formularios
      const forms = await getForms();
      // Buscar el formulario según el tipo
      const form = forms.find((f) => f.type === formType);

      if (form?.id) {
        // Obtener las preguntas del formulario
        const response = await getQuestionsByForm(form.id);
        setQuestions(response || []);
      } else {
        console.error(`Formulario no encontrado para el tipo: ${formType}`);
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error al cargar las preguntas:", error);
    } finally {
      setLoading(false);
    }
  }, [formType]);

  // Cargar preguntas inicialmente
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // Registrar la función de recarga en el estado global
  useEffect(() => {
    setRefreshQuestions(fetchQuestions);
    
    return () => {
      // Limpiar al desmontar
      setRefreshQuestions(null);
    };
  }, [fetchQuestions, setRefreshQuestions]);

  const formatQuestions = (questions) => {
    return (questions || []).map((question) => {
      // Trazas para depuración
      const texts = question?.texts;
      const language = selectedLanguage;


      const matchedText = Array.isArray(texts)
          ? texts.find((t) => t.language === language)?.text
          : null;


      return {
        id: question?.id || null,
        questions: matchedText || "not text in this language",
        questionType: question?.type || "Unknown",
        possibleAnswers: Array.isArray(question?.options)
            ? question.options
            .map((option) =>
                Array.isArray(option?.text)
                    ? option.text.find((t) => t.language === language)?.text || null
                    : null
            )
            .filter(Boolean)
            .join(", ") || ""
            : "",
        active: question?.active ? "Sí" : "No",
        screenNumber: question?.screen,
      };
    });
  };


  const formattedQuestions = formatQuestions(questions);

  const closeModal = () => {
    setUpdateModal(false);
  };

  return (
      <section className="w-full">
        <Toaster />

        <section className="w-full flex mb-3">
          <Dialog
            open={createModal}
            onOpenChange={(isOpen) => setCreateModal(isOpen)}
          >
            <DialogTrigger asChild={true}>
              <Button className="mt-4 bg-primary hover:bg-primary-600">
                {t("buttonTexts.addNew")} <Plus className="ml-2" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[625px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {t("MainLayout.questionForms.createQuestion")}
                </DialogTitle>
              </DialogHeader>
              <CreateQuestion 
                formType={formType} 
                closeModal={() => setCreateModal(false)} 
                fetchQuestions={fetchQuestions} 
              />
            </DialogContent>
          </Dialog>
        </section>

        <section className="w-full flex">
          <Dialog
              open={updateModal}
              onOpenChange={(isOpen) => setUpdateModal(isOpen)}
          >
            <DialogContent className="sm:max-w-[625px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {t("MainLayout.questionForms.updateQuestion")}
                </DialogTitle>
              </DialogHeader>
              <UpdateQuestion formType={formType} closeModal={closeModal} />
            </DialogContent>
          </Dialog>
        </section>

        {loading ? (
            <div className="flex justify-center">
              <Loader style={"loader"} />
            </div>
        ) : (
            <DataTable
                data={formattedQuestions}
                columns={columnsToRender}
                mainFilterColumn="questions"
                textPlaceholder={t("filterTextPlaceholders.question")}
            />
        )}
      </section>
  );
};

export default FormsQuestions;
